import { useEffect, useState } from 'react';
import { ApiResponse } from '../../../types/ApiResponse';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';

interface FetcherRenderArgs<T> {
  loadState: LoadState;
  setLoadState: (value: LoadState) => void;
  error: FormError;
  setError: (value: FormError) => void;
  data: T;
  setData: (value: T) => void;
  fetchData: (useExternalLoadState?: boolean) => Promise<void>;
}

type FetchFnArgs = any[];

interface FetcherProps<T> {
  render: (args: FetcherRenderArgs<T>) => React.ReactNode;
  fetchFn: (...args: FetchFnArgs) => Promise<ApiResponse<T>>;
  fetchFnArgs?: FetchFnArgs;
  defaultValue?: T;
  processData?: (data: T) => T;
}

export const Fetcher = <T,>({
  render,
  fetchFn,
  defaultValue,
  fetchFnArgs = [],
  processData,
}: FetcherProps<T>) => {
  const [error, setError] = useState<FormError>();
  const [loadState, setLoadState] = useState(LoadState.Idle);
  const [data, setData] = useState<T>(defaultValue);

  useEffect(() => {
    fetchData(false);
  }, []);

  const fetchData = async (useExternalLoadState: boolean) => {
    setError(undefined);
    !useExternalLoadState && setLoadState(LoadState.Loading);
    const response = await fetchFn(...fetchFnArgs);

    if (!response.success) {
      !useExternalLoadState && setLoadState(LoadState.Error);
      return setError({
        severity: 'error',
        text: `Fel vid hämtning av sparade värderingar: ${response.error}`,
      });
    }
    !useExternalLoadState && setLoadState(LoadState.Success);
    setData(processData ? processData(response.data) : response.data);
  };

  return (
    <>
      {render({
        loadState,
        setLoadState,
        error,
        setError,
        data,
        setData,
        fetchData,
      })}
    </>
  );
};
